import { Flex, IconButton, Text } from '@chakra-ui/react';
import { functionalUpdate, PaginationState, Table, Updater } from '@tanstack/react-table';
import { useCallback, useState } from 'react';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';

export const PAGE_SIZE = 50;

// État de pagination d'un tableau. Après une suppression ou un filtre, la page courante peut
// ne plus exister : la dernière page est alors affichée directement, sans rendu vide
export function usePagination(rowCount: number) {
	const [ stored, setStored ] = useState<PaginationState>({ pageIndex: 0, pageSize: PAGE_SIZE });
	const lastPage = Math.max(Math.ceil(rowCount / stored.pageSize) - 1, 0);
	const pagination = stored.pageIndex > lastPage ? { ...stored, pageIndex: lastPage } : stored;
	const setPagination = useCallback(
		(updater: Updater<PaginationState>) =>
			setStored((current) =>
				functionalUpdate(updater, { ...current, pageIndex: Math.min(current.pageIndex, lastPage) })
			),
		[ lastPage ]
	);
	return [ pagination, setPagination ] as const;
}

// Pagination sous un tableau ; masquée quand tout tient sur une page
export default function TablePagination<T>(props: { table: Table<T> }) {
	const { table } = props;
	const { pageIndex, pageSize } = table.getState().pagination;
	const pageCount = table.getPageCount();
	const total = table.getPrePaginationRowModel().rows.length;

	if (pageCount <= 1) {
		return null;
	}
	const first = pageIndex * pageSize + 1;
	const last = Math.min((pageIndex + 1) * pageSize, total);
	return (
		<Flex px='25px' pb='20px' justify='flex-end' align='center' gap='8px'>
			<Text color='secondaryGray.600' fontSize='sm' me='8px'>
				{first}–{last} sur {total}
			</Text>
			<IconButton
				aria-label='Page précédente'
				icon={<MdChevronLeft />}
				size='sm'
				variant='outline'
				onClick={() => table.previousPage()}
				isDisabled={!table.getCanPreviousPage()}
			/>
			<IconButton
				aria-label='Page suivante'
				icon={<MdChevronRight />}
				size='sm'
				variant='outline'
				onClick={() => table.nextPage()}
				isDisabled={!table.getCanNextPage()}
			/>
		</Flex>
	);
}
